import { useState, useEffect, useCallback } from 'react';
import { apiRawFetch } from '../../main/client';

export interface ExpenseCategory {
    id: number;
    name: string;
    type: 'FIXED' | 'VARIABLE' | 'INCOME';
    propertyId: number | null;
    icon: string | null;
    color: string | null;
    isSystem: boolean;
}

interface UseExpenseCategoriesResult {
    categories: ExpenseCategory[];
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
    createCategory: (name: string, type: ExpenseCategory['type']) => Promise<ExpenseCategory | null>;
    deleteCategory: (categoryId: number) => Promise<boolean>;
}

const useExpenseCategories = (propertyId: number | undefined): UseExpenseCategoriesResult => {
    const [categories, setCategories] = useState<ExpenseCategory[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchCategories = useCallback(async () => {
        if (!propertyId) {
            setCategories([]);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const response = await apiRawFetch(`api/properties/${propertyId}/expense-categories`);

            if (!response.ok) {
                throw new Error('Failed to fetch expense categories');
            }

            const { data } = await response.json();
            setCategories(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    }, [propertyId]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const createCategory = useCallback(async (name: string, type: ExpenseCategory['type']): Promise<ExpenseCategory | null> => {
        if (!propertyId) return null;

        try {
            const response = await apiRawFetch(
                `api/properties/${propertyId}/expense-categories`,
                'POST',
                { name, type }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to create category');
            }

            const { data } = await response.json();
            await fetchCategories();
            return data;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
            return null;
        }
    }, [propertyId, fetchCategories]);

    const deleteCategory = useCallback(async (categoryId: number): Promise<boolean> => {
        if (!propertyId) return false;

        try {
            const response = await apiRawFetch(
                `api/properties/${propertyId}/expense-categories/${categoryId}`,
                'DELETE'
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to delete category');
            }

            await fetchCategories();
            return true;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
            return false;
        }
    }, [propertyId, fetchCategories]);

    return {
        categories,
        loading,
        error,
        refresh: fetchCategories,
        createCategory,
        deleteCategory,
    };
};

export default useExpenseCategories;
